import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';

/**
 * "Today" for all daily AI content is Europe/Oslo local time, not the
 * visitor's own timezone or UTC - otherwise different components (and
 * visitors outside Norway) can disagree on what day it is near midnight.
 */
export function getOsloDateKey(date: Date = new Date()): string {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Oslo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}

export type DailyAiContentSource = 'cache' | 'db' | 'ai' | 'fallback';

export interface DailyAiContentConfig<T> {
    /** Used as the Supabase `daily_content.type` column and the localStorage key prefix. */
    type: string;
    /** Calls the AI (via /api/gemini) and returns fresh content. Must throw/reject on failure. */
    generate: () => Promise<T>;
    /** Returns a local-only fallback value. Never persisted to the shared Supabase cache. */
    fallback: () => T;
    /** Rejects corrupted cached/db values (e.g. truncated or malformed AI output). Defaults to always valid. */
    isValid?: (value: T) => boolean;
    /** Override "today" - mainly useful for tests. Defaults to the current Europe/Oslo date. */
    dateKey?: string;
}

/**
 * Shared "localStorage -> Supabase -> AI generate -> cache" pipeline used by
 * every piece of daily AI content on the site (Dagens Skjeta-Faktum, City
 * Lore, the news ticker, ...).
 *
 * Only genuinely AI-generated content is written back to the shared
 * `daily_content` table - a fallback value must never get locked in as
 * "today's content" for every visitor just because generation failed once.
 */
export async function loadDailyAiContent<T>(
    config: DailyAiContentConfig<T>,
    force: boolean = false
): Promise<{ value: T; source: DailyAiContentSource }> {
    const { type, generate, fallback, isValid = () => true } = config;
    const dateKey = config.dateKey ?? getOsloDateKey();
    const localKey = `skjeta_${type}_${dateKey}`;

    if (!force) {
        const cached = localStorage.getItem(localKey);
        if (cached) {
            try {
                const parsed = JSON.parse(cached) as T;
                if (isValid(parsed)) {
                    return { value: parsed, source: 'cache' };
                }
            } catch {
                // corrupted cache entry, fall through to regeneration
            }
            localStorage.removeItem(localKey);
        }

        try {
            const { data } = await supabase
                .from('daily_content')
                .select('content')
                .eq('date', dateKey)
                .eq('type', type)
                .single();

            if (data?.content && isValid(data.content as T)) {
                const value = data.content as T;
                localStorage.setItem(localKey, JSON.stringify(value));
                return { value, source: 'db' };
            }
        } catch {
            // no row for today yet - fall through to generation
        }
    }

    try {
        const value = await generate();
        localStorage.setItem(localKey, JSON.stringify(value));

        if (!force) {
            supabase
                .from('daily_content')
                .upsert({ date: dateKey, type, content: value }, { onConflict: 'date, type' })
                .then(({ error }) => {
                    if (error) console.warn(`Failed to persist daily content "${type}"`, error.message);
                });
        }

        return { value, source: 'ai' };
    } catch (e) {
        console.error(`AI generation failed for "${type}"`, e);
        const value = fallback();
        localStorage.setItem(localKey, JSON.stringify(value));
        return { value, source: 'fallback' };
    }
}

export interface DailyAiContentState<T> {
    data: T | null;
    loading: boolean;
    source: DailyAiContentSource | null;
}

/** React wrapper around {@link loadDailyAiContent} that loads once on mount. */
export function useDailyAiContent<T>(config: DailyAiContentConfig<T>) {
    const [state, setState] = useState<DailyAiContentState<T>>({ data: null, loading: true, source: null });

    // Keep the latest config available to `load` without re-triggering the
    // mount effect every time a caller passes a fresh inline function.
    const configRef = useRef(config);
    configRef.current = config;

    const load = useCallback(async (force: boolean = false) => {
        setState((s) => ({ ...s, loading: true }));
        const { value, source } = await loadDailyAiContent(configRef.current, force);
        setState({ data: value, loading: false, source });
    }, []);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { ...state, refresh: load };
}
